import type { ReactNode } from 'react';
import { Tag } from '../../components/ui';
import type { ReportStatus } from '../../lib/financeTypes';
import { money } from '../../lib/format';

const STATUS_LABEL: Record<ReportStatus, string> = {
  draft: 'Draft',
  processing: 'Processing',
  'review-required': 'Review required',
  approved: 'Approved',
  rejected: 'Rejected',
  failed: 'Failed',
  superseded: 'Superseded',
};
const STATUS_TONE: Record<ReportStatus, 'neutral' | 'info' | 'pend' | 'ok' | 'bad'> = {
  draft: 'neutral',
  processing: 'info',
  'review-required': 'pend',
  approved: 'ok',
  rejected: 'bad',
  failed: 'bad',
  superseded: 'neutral',
};

export function StatusTag({ status }: { status: ReportStatus }) {
  return <Tag tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Tag>;
}

export const usd = (value: number | null | undefined, cents = false) =>
  value === null || value === undefined ? '—' : money(value, { cents });

export const pctLabel = (value: number | null | undefined, digits = 1) =>
  value === null || value === undefined ? '—' : `${(value * 100).toFixed(digits)}%`;

/** A row of pill buttons for choosing one value. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div className="chip-row" role="group" aria-label={label} style={{ margin: 0 }}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`chip${option.value === value ? ' on' : ''}`}
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Marks a figure as an estimate rather than something read from a confirmed statement. */
export function Estimate({ children }: { children?: ReactNode }) {
  return (
    <span className="tag info" title="Calculated, not read directly from a statement" style={{ marginLeft: 6 }}>
      {children ?? 'Estimate'}
    </span>
  );
}

export function Metric({
  label,
  value,
  hint,
  tone,
  estimate = false,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: 'ok' | 'bad' | 'warn';
  estimate?: boolean;
}) {
  return (
    <div className="stat">
      <span className={`value${tone ? ` ${tone}` : ''}`}>{value}</span>
      <span className="label">
        {label}
        {estimate && <Estimate />}
      </span>
      {hint && <span className="mut" style={{ fontSize: 12 }}>{hint}</span>}
    </div>
  );
}

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function Slider({
  label,
  value,
  min,
  max,
  step,
  display,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  display: string;
  onChange: (value: number) => void;
}) {
  return (
    <label className="field">
      <span className="label-text">{label}</span>
      <div className="row between" style={{ gap: 10 }}>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          style={{ flex: 1 }}
          aria-valuetext={display}
        />
        <span className="nowrap" style={{ minWidth: 72, textAlign: 'right' }}>{display}</span>
      </div>
    </label>
  );
}
